/**
 * RatingsCard — TEAM-visible interaction-rating history for one contact
 * (Expansion Phase G): stars, note, recruiter name, relTime, and a phone icon
 * when the rating is linked to a call. Star corrections are a NEW rating (no
 * star edit anywhere); the author may delete their own rating within 24h, CRM
 * admins always can. Notes describe the INTERACTION (phone manner,
 * reachability) — never medical content.
 */
import { useCallback, useEffect, useState } from 'react';
import { Phone, Star, Trash2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';
import { InteractionRating } from '../../types';
import { relTime, useIsCRMAdmin, useTeam } from '../../lib';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

const DAY_MS = 24 * 3600 * 1000;

export function Stars({ value, size = 'h-3.5 w-3.5' }: { value: number; size?: string }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${size} ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`}
          aria-hidden="true"
        />
      ))}
    </span>
  );
}

interface Props {
  contactId: string;
  reloadToken: number;
}

export default function RatingsCard({ contactId, reloadToken }: Props) {
  const { user } = useAuth();
  const isAdmin = useIsCRMAdmin();
  const { byId } = useTeam();

  const [ratings, setRatings] = useState<InteractionRating[]>([]);
  const [average, setAverage] = useState<number | null>(null);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [armedDelete, setArmedDelete] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/crm/contacts/${contactId}/ratings`);
      setRatings(res.data?.ratings || []);
      setAverage(res.data?.average ?? null);
      setCount(res.data?.count || 0);
    } catch {
      /* decorative history — renders what it has; next reloadToken retries */
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load, reloadToken]);

  const canDelete = (r: InteractionRating) =>
    isAdmin || (r.recruiter_user_id === user?.id && Date.now() - new Date(r.created_at).getTime() < DAY_MS);

  /** Two-tap delete (arm 3s, then delete; optimistic with revert). */
  const remove = async (r: InteractionRating) => {
    if (armedDelete !== r.id) {
      setArmedDelete(r.id);
      window.setTimeout(() => setArmedDelete((a) => (a === r.id ? null : a)), 3000);
      return;
    }
    setArmedDelete(null);
    setActionError(null);
    setRatings((p) => p.filter((x) => x.id !== r.id));
    try {
      await api.delete(`/api/crm/ratings/${r.id}`);
      load(); // refresh the average/count
    } catch (e: any) {
      // Re-insert only the failed row (newest-first order), never a snapshot.
      setRatings((p) => [...p, r].sort((a, b) => b.created_at.localeCompare(a.created_at)));
      setActionError(errMsg(e));
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <Star className="h-4 w-4 text-amber-500" />
          Call ratings
        </h3>
        {count >= 1 && average !== null && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            ★{average.toFixed(1)} · {count}
          </span>
        )}
      </div>

      {actionError && (
        <p role="alert" className="mb-2 text-xs text-red-600 dark:text-red-400">{actionError}</p>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : ratings.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No call ratings yet — a prompt appears after each connected call.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {ratings.map((r) => {
            const recruiter = r.recruiter_user_id ? byId.get(r.recruiter_user_id)?.name : null;
            return (
              <li key={r.id} className="py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex min-w-0 items-center gap-2">
                    <Stars value={r.rating} />
                    {r.communication_id && (
                      <Phone className="h-3 w-3 shrink-0 text-gray-400" aria-label="Linked to a call" />
                    )}
                  </span>
                  {canDelete(r) && (
                    <button
                      type="button"
                      onClick={() => remove(r)}
                      aria-label={armedDelete === r.id ? 'Confirm delete rating' : 'Delete rating'}
                      title={armedDelete === r.id ? 'Click again to confirm' : 'Delete'}
                      className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-1 transition-colors duration-150 sm:min-h-0 sm:min-w-0 ${
                        armedDelete === r.id
                          ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                          : 'text-gray-400 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-700'
                      }`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {r.note && (
                  <p className="mt-1 break-words text-sm text-gray-700 dark:text-gray-300">{r.note}</p>
                )}
                <p className="mt-0.5 text-xs text-gray-400">
                  {recruiter || 'Unknown recruiter'} · {relTime(r.created_at)}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
